<template>
  <div
    class="input"
    :class="[
      {
        'input--error': error,
        'input--disabled': disabled,
      }
    ]"
    :style="{
      width: width
    }"
  >
    <input
      :id="name"
      ref="inputRef"
      :class="[
        `input-${size}`,
        {
          'filled': filled
        }
      ]"
      :name="name"
      :type="type"
      :disabled="disabled"
      :style="{
        width: width,
        paddingRight: paddingRight
      }"
      :autocomplete="autocomplete"
      :placeholder="placeholderMask"
      :value="inputValue"
      @blur="emit('blur', $event)"
      @change="handleChange"
      @input="handleInput"
      @focus="emit('focus', $event)"
    >
    <div
      v-if="hasPlaceholder"
      class="input__label"
      :class="[
        `input__label--${size}`,
        {
          'input__label--disabled': disabled
        }
      ]"
    >
      {{ inputPlaceholder }}
    </div>

    <div
      v-show="loading"
      :class="['input__loader', `input__loader--${size}`]"
    >
      <BaseLoader size="sm" />
    </div>
    <slot name="extra" />
    <slot name="errors" />
  </div>
</template>

<script setup lang="ts">
type Props = {
  modelValue: string | number | null
  size?: 'small' | 'medium' | 'large'
  type?: string
  placeholder?: string
  placeholderMask?: string
  error?: boolean
  disabled?: boolean
  required?: boolean
  width?: string
  paddingRight?: string
  autocomplete?: string
  name?: string
  loading?: boolean
}

type Emits = {
  (e: 'update:modelValue' | 'input', value: string): void
  (e: 'change'): void
  (e: 'blur' | 'focus', event: FocusEvent): void
}

const props = withDefaults(defineProps<Props>(), {
  size: 'medium',
  value: '',
  type: 'text',
  width: '100%',
  paddingRight: '12px',
  autocomplete: 'on',
  name: '',
  placeholder: '',
  placeholderMask: '',
  loading: false
})
const emit = defineEmits<Emits>()

const input = useTemplateRef<HTMLInputElement>('inputRef')

const isValueString = computed(() => {
  return props.type === 'text'
})

const inputValue = computed({
  get: () => props.modelValue || '',
  set: (value: string) => {
    const pushValue: string = isValueString.value
      ? (value as string).trimStart()
      : value

    emit('update:modelValue', pushValue)
    emit('input', pushValue)
  }
})

const filled = computed(() => {
  return inputValue.value
})

const inputPlaceholder = computed(() => {
  const { required, placeholder } = props

  return required ? `${placeholder} *` : placeholder
})

const hasPlaceholder = computed(() => {
  return props.placeholder && props.placeholder.length > 0
})

const handleInput = (event: Event): void => {
  inputValue.value = (event.target as HTMLInputElement).value
}

const handleChange = () => {
  emit('change')
}

const onFocus = () => {
  nextTick(() => {
    input.value?.focus()
  })
}

const onBlur = () => {
  input.value?.blur()
}

defineExpose({
  onFocus,
  onBlur
})
</script>

<style lang="scss">
.input {
  position: relative;
  $root: &;

  &__loader {
    position: absolute;
    top: -10px;
    left: 0;
    right: 0;
    background: rgba(255, 255, 255, .6);
    height: calc(4.2rem + 10px);
    padding-top: 10px;

    &--large {
      height: calc(4.8rem + 10px);
    }

    &--small {
      height: calc(3.2rem + 10px);
    }
  }

  &__label {
    font-size: 1.4rem;
    line-height: $line-height-18;
    margin-bottom: .5rem;
    position: absolute;
    color: #8A8A8A;
    padding: 0 .5rem;
    display: inline-block;
    pointer-events:none;
    left: 0.7rem;
    top: 2.1rem;
    transform: translateY(-50%);
    transition: top 0.2s ease, left 0.2s ease;
    background-color: #fff;
    display: inline-block;
    width: calc(100% - 1.4rem);
    text-align: left;

    &--disabled {
      background-color: #f3f3f3;
      border-radius: 4px;
    }

    &--large {
      font-size: 1.6rem;
      top: 2.3rem;

      @media only screen and (max-width: 740px) {
        font-size: 1.4rem;
      }
    }

    &--small {
      font-size: 1.4rem;
      top: 1.5rem;

      @media only screen and (max-width: 740px) {
        font-size: 1.2rem;
      }
    }
  }

  input {
    font-family: 'Open Sans', sans-serif;
    height: 4.2rem;
    font-size: 1.6rem;
    border-radius: 4px;
    border: 1px solid #e7e7e7;
    padding: 0 1.2rem;
    -moz-appearance: textfield;
    transition: border-color $transition-time;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;

    &.input-large {
      height: 4.8rem;
    }

    &.input-small {
      height: 3.2rem;
      font-size: 1.4rem;
    }

    &:focus,
    &:hover {
      outline: none;
      border-color: rgba(#161616, .5);
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
    }

    &:focus,
    &.filled {

      ~ #{$root}__label {
        font-size: 1.2rem;
        top: 0rem;
        width: initial;
      }
    }

    &::-webkit-input-placeholder {
        color: #8A8A8A;
      }
    &::-moz-placeholder {
      color: #8A8A8A;
    }
    &:-ms-input-placeholder {
      color: #8A8A8A;
    }
    &:-moz-placeholder {
      color: #8A8A8A;
    }
    &:-webkit-autofill {
      -webkit-box-shadow: inset 0 0 0 2.1rem #fff !important;
    }

  }

  &--disabled {
    input {
      background-color: #f3f3f3;
      border-color: #E7E7E7;
      cursor: default;

      &:hover {
        background-color: #f3f3f3;
        border-color: #E7E7E7;
      }

      &::-webkit-input-placeholder {
        color: #E7E7E7;
      }
      &::-moz-placeholder {
        color: #E7E7E7;
      }
      &:-ms-input-placeholder {
        color: #E7E7E7;
      }
      &:-moz-placeholder {
        color: #E7E7E7;
      }
    }
  }

  &--error {
    input {
      border-color: $error-color;

      &:hover,
      &:focus {
        border-color: $error-color;
      }
    }

    #{$root}__label {
      color: $error-color;
    }
  }

  [class^="icon-"],
  [class*=" icon-"] {
    position: absolute;
    right: 13px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 1.6rem;
  }
}
</style>
